/**
 * GameApi — dev 도구(AutoPlay/DebugDashboard/CheatModal) 전용 게임 조작 표면 (boot 패러다임 v2 규칙 4).
 *
 * - **구현 0줄** — 커맨드/Flow 메서드 참조 테이블만. 도메인 boot이 자기 슬라이스를 api.register()로
 *   기여하고, GameScene은 부팅 말미 build()로 합성해 `scene.api`로 노출한다 (create()마다 재조립 —
 *   registry 보관 금지: scene.restart 중 stale 윈도).
 * - 소비자는 dev 도구만 — 게임 뷰가 GameApi에 접근 금지 (service locator화 차단).
 * - 신규 도메인의 dev 액션 추가 = GameApi 키 선언 1줄 + boot의 register 1곳. GameScene 무접촉.
 * - 망라성 2중 게이트: GAME_API_KEYS satisfies(키 목록 누락 = 컴파일 에러) + build()의 미등록 throw
 *   (test:smoke 실부팅이 CI에서 검출).
 */
import type { CheatActions } from "../systems/CheatActions.js";
import type { AutoPlaySnapshot } from "./buildAutoPlaySnapshot.js";
import type { TabId } from "../ui/tabVisibility.js";
import type { ModuleInstance } from "../models/ModuleInstance.js";
import type { ModulePiece } from "../data/modulePieces.js";
import type { ModuleRarity } from "../modules/ProjectileModule.js";
import type { ModuleInfoAction } from "../ui/ModuleInfoPopup.js";

export interface GameApi {
  // ── core/dev ──
  getAutoPlaySnapshot(): AutoPlaySnapshot;
  openTab(tab: TabId): void;
  resetSave(): void;
  cheats: CheatActions;
  // ── tutorial ──
  startTutorial(scenarioId?: string): void;
  resetTutorial(scenarioId?: string): void;
  // ── economy/iap ──
  buyGoldBox(): void;
  /** 골드 상자 10연 구매 (누진 단가 × 할인 배수, 공용 천장 누적). */
  buyGoldBox10(): void;
  buyDiamondBox(): void;
  buyRebirthConditionReset(): void;
  buyRemoveAds(): void;
  /** 광고 제거 + 다이아 패키지 구매 (단독과 상호배타). */
  buyRemoveAdsPackage(): void;
  // ── progression ──
  performRebirth(): void;
  upgradeRebirthStat(statId: string): void;
  /** 환생 포인트 1 → 강화석 N 교환 (강화석 수급 P1). */
  exchangeRebirthPointForStone(): void;
  acquireRelic(relicId: string): void;
  // ── battle/grid ──
  grantModule(
    definitionId: string,
    pieceOverride?: ModulePiece,
    rarityOverride?: ModuleRarity,
  ): ModuleInstance | null;
  placeModule(x: number, y: number, instanceId: string): void;
  moveModule(fromX: number, fromY: number, toX: number, toY: number): void;
  removeModuleAt(x: number, y: number): void;
  /** 모두 장착 해제 — 배치된 모든 모듈을 인벤토리로 회수(해금 유지). */
  unequipAllModules(): void;
  /** 추천 장착 — 보유 미배치 모듈을 높은 등급순으로 빈 칸에 최대한 채움(기존 배치 유지). */
  recommendEquipModules(): void;
  unlockCell(x: number, y: number): void;
  /** 모듈 레벨업 — moduleId(definitionId)의 플레이어 레벨 +1 (강화석 전용, 같은 moduleId 전체 반영). */
  upgradeModule(moduleId: string): void;
  openModuleInfo(instanceId: string, action?: ModuleInfoAction): void;
  // ── compose (합성/융합) ──
  performModuleMerge(materialIds: readonly string[]): void;
  performModuleFusion(materialIds: readonly string[]): void;
  // ── dungeon ──
  challengeDungeon(): void;
  sweepDungeon(): void;
  // ── dailyShop ──
  buyDailyShopSlot(index: number): void;
  rerollDailyShop(): void;
  // ── shop ad diamond (광고 다이아) ──
  /** 상점 광고 다이아 구매 — id="ad_diamond_cd"(2시간 쿨타임)/"ad_diamond_daily"(오늘 1회). */
  buyShopAdDiamond(id: string): void;
  /** 치트/QA — 광고 다이아 재구매 게이트 전체 해제(쿨타임 clear + 오늘 수령 플래그 복귀). */
  resetShopAdGates(): void;
  // ── settings ──
  toggleSettings(): void;
  // ── perf (성능 오버레이) ──
  /** 성능 프로파일러 오버레이 토글 — 새 상태(true=ON) 반환. */
  togglePerfOverlay(): boolean;
  /** 현재 성능 지표(FPS·heap·섹션ms·객체수)를 콘솔로 출력 (AutoPlay 캡처). */
  dumpPerfStats(): void;
}

/** GameApi 키 목록 — build() 망라성 런타임 게이트용 SSoT. */
export const GAME_API_KEYS = [
  "getAutoPlaySnapshot",
  "openTab",
  "resetSave",
  "cheats",
  "startTutorial",
  "resetTutorial",
  "buyGoldBox",
  "buyGoldBox10",
  "buyDiamondBox",
  "buyRebirthConditionReset",
  "buyRemoveAds",
  "buyRemoveAdsPackage",
  "performRebirth",
  "upgradeRebirthStat",
  "exchangeRebirthPointForStone",
  "acquireRelic",
  "grantModule",
  "placeModule",
  "moveModule",
  "removeModuleAt",
  "unequipAllModules",
  "recommendEquipModules",
  "unlockCell",
  "upgradeModule",
  "openModuleInfo",
  "performModuleMerge",
  "performModuleFusion",
  "challengeDungeon",
  "sweepDungeon",
  "buyDailyShopSlot",
  "rerollDailyShop",
  "buyShopAdDiamond",
  "resetShopAdGates",
  "toggleSettings",
  "togglePerfOverlay",
  "dumpPerfStats",
] as const satisfies readonly (keyof GameApi)[];

/** 누락 게이트 — GameApi에 키를 추가하고 목록에 빠뜨리면 이 상수가 그 키 리터럴이 되어 컴파일 에러. */
type MissingApiKey = Exclude<keyof GameApi, (typeof GAME_API_KEYS)[number]>;
export const gameApiKeysExhaustive: [MissingApiKey] extends [never] ? true : MissingApiKey = true;

/**
 * api 슬라이스 수집기 — boot들이 register로 기여, 부팅 말미 build()가 합성.
 * 중복 키 = 즉시 throw (조립 오류 조기 실패), 미등록 키 = build()에서 throw (망라성).
 */
export class GameApiRegistry {
  private readonly entries = new Map<keyof GameApi, GameApi[keyof GameApi]>();

  register<K extends keyof GameApi>(slice: Pick<GameApi, K>): void {
    for (const key of Object.keys(slice) as K[]) {
      if (this.entries.has(key)) {
        throw new Error(`[GameApi] 중복 등록 키: ${String(key)}`);
      }
      this.entries.set(key, slice[key]);
    }
  }

  build(): GameApi {
    const missing = GAME_API_KEYS.filter((key) => !this.entries.has(key));
    if (missing.length > 0) {
      throw new Error(`[GameApi] 미등록 키: ${missing.join(", ")} — 담당 boot의 api.register 누락`);
    }
    return Object.fromEntries(this.entries) as unknown as GameApi;
  }
}
